'use strict';

import {createElWithAttrs, callbackOnElements} from '@borngroup/born-utilities';
import {elConsumesSpace} from '../helpers/utilities';

export const
    FORM_SELECTOR = 'form, [data-validation-component="form"]',
    FIELD_SELECTOR = 'input, textarea, select',
    FIELD_INVALID_CLASS = 'is-invalid',
    FEEDBACK_SUCCESS_CLASS = 'success-feedback',
    FEEDBACK_INVALID_CLASS = 'invalid-feedback',
    FEEDBACK_VISIBILITY_CLASS = 'set--visible',
    VALIDATION_LIST = {},
    VALIDATION_MESSAGES = {};

//Set a global flag to handle when a group of fields are validated at the same time.
//Use this flag to mark the first field that was validated and focus on it.
let isFirstInvalidField = true;

/**
 * Return the matching selector `el` form if it is a string.
 */
function _getFormElement(el) {
    return typeof el === 'string' ? document.querySelector(el) : el;
}

/**
 * Returns the list of form input fields within a given container.
 * This fixes cases where a non-HTMLFormElement is used, which does not have access to the `form.elements` property.
 */
function _getFormFields(containerEl) {
    return _getFormElement(containerEl).querySelectorAll(FIELD_SELECTOR);
}

/**
 * Validates all input fields within the provided form by calling validator.checkFormValidity(form),
 * with the intention of displaying any error styles and messages.
 * @param {event} evt - Event to be canceled if form is invalid.
 * @returns {boolean} - Flag to indicate if form is valid
 */
function _validateForm(evt) {
    let valid = true;

    if (!checkFormValidity(this)) {
        valid = false;

        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
            evt.stopImmediatePropagation();
        }
    }

    return valid;
}

function _getFeedbackEl(field) {
    let describedByID = field.getAttribute('aria-describedby');

    return document.querySelector(`#${describedByID}`) || createElWithAttrs(field.parentNode, {id: describedByID, class: FEEDBACK_INVALID_CLASS, role: 'alert'});
}

/**
 * Set an `aria-describedby` attribute if none is present.
 */
function _generateFeedbackEl(field) {
    if (!field.getAttribute('aria-describedby')) {
        //Prevent generating an ID off of an invalid input name.
        let cleanID = (field.name || field.id || '').replace(/\W/g, '');

        field.setAttribute('aria-describedby', `${cleanID || 'inputField'}-${Math.floor(new Date().getTime() * Math.random()).toString()}`);

        //Append the feedback element if it doesn't already exist.
        _getFeedbackEl(field);
    }
}

/**
 * This method should only be called by the browser when an entire form's validation fails.
 * Prevents displaying browser's native error tooltip/bubble.
 * The other purpose of this additional private method is to prevent
 * re-attaching the same handler when re-initializing validation.
 */
function _validateAndPreventDefault(evt) {
    evt.preventDefault();

    //Ensure focus is set to the first invalid field.
    //This is necessary since this method is called after a form is attempted to be submited,
    //and there doesn't seem to be an OOTB form event for this.
    validateField(this, {setFocusOnFirst: true});
}

/**
 * Validate on user input if `currentField` is already invalid.
 * The other purpose of this additional private method is to prevent
 * re-attaching the same handler when re-initializing validation.
 */
function _validateIfInvalid() {
    if (this.classList.contains(FIELD_INVALID_CLASS)) {
        validateField(this);
    }
}

/**
 * Checks if a `validationName` matches a field's class, attribute, or selector.
 */
function _matchesValidationSelector(field, validationName) {
    return field.classList.contains(validationName) || field.hasAttribute(validationName) || field.matches(validationName);
}

/**
 * Actual custom error object validation.
 * containerEl: the form or field container.
 * errorGroup: an object literal.
 */
function _setCustomErrorsByGroup(containerEl, errorGroup) {
    for (let fieldName in errorGroup) {
        let matchedEl = containerEl.querySelector(`[name="${fieldName}"]`);

        displayValidity(matchedEl, errorGroup[fieldName]);
    }
}

/**
 * Configure override validation messaging at the element level.
 */
function _getOverrideValidationMessage(field) {
    let validationMessage;

    if (field.validity.patternMismatch && (field.getAttribute('data-pattern-mismatch') || VALIDATION_MESSAGES['patternMismatch'])) {
        validationMessage = field.getAttribute('data-pattern-mismatch') || VALIDATION_MESSAGES['patternMismatch'];
    }
    if ((field.validity.rangeOverflow || field.validity.rangeUnderflow) && (field.getAttribute('data-range-error') || VALIDATION_MESSAGES['rangeError'])) {
        validationMessage = field.getAttribute('data-range-error') || VALIDATION_MESSAGES['rangeError'];
    }
    if ((field.validity.tooLong || field.validity.tooShort) && (field.getAttribute('data-range-error') || VALIDATION_MESSAGES['rangeError'])) {
        validationMessage = field.getAttribute('data-range-error') || VALIDATION_MESSAGES['rangeError'];
    }
    if (field.validity.typeMismatch && (field.getAttribute('data-type-error') || VALIDATION_MESSAGES['typeMismatch'] || VALIDATION_MESSAGES['invalidEmail'])) {
        validationMessage = field.getAttribute('type') === 'email' ? VALIDATION_MESSAGES['invalidEmail'] : field.getAttribute('data-type-error') || VALIDATION_MESSAGES['typeMismatch'];
    }
    if (field.validity.valueMissing && (field.getAttribute('data-missing-error') || VALIDATION_MESSAGES['valueMissing'] || VALIDATION_MESSAGES['emptyRadio'])) {
        validationMessage = field.getAttribute('type') === 'radio' ? VALIDATION_MESSAGES['emptyRadio'] : field.getAttribute('data-missing-error') || VALIDATION_MESSAGES['valueMissing'];
    }

    return validationMessage;
}

/**
 * Checks wether or not a field should be validated, fields are skipped if they're set to display: none; or if they occopy no space.
 * For example, this prevents validating field containers that are inside a hidden tab in a tabbed interface.
 *
 * If you'd still like to process validation for hidden fields, add the `data-validate-hidden` attribute said fields.
 * @param  {[type]} field [description]
 * @return {[type]}       [description]
 */
function _canValidateField(field) {
    return field.hasAttribute('data-validate-hidden') || elConsumesSpace(field);
}

/**
 * Check an individual field's validity.
 * If the field has custom error  messages, display them.
 * If field is valid, clear all errors.
 * @return {[type]} [description]
 */
export function validateField(field, options = {}) {
    field.setCustomValidity('');

    //Checks if there are any custom validations that match the current field.
    getCustomValidation(field);

    if (!field.validity.valid && _canValidateField(field)) {
        let validationMessage = field.validationMessage;

        //Ensures the form's success feedback elements are hidden whenever there's an error.
        clearFormSuccess(field.form);

        if (!field.validity.customError) {
            validationMessage = _getOverrideValidationMessage(field) || validationMessage;
        }

        if (!options.silent) {
            displayValidity(field, validationMessage);
        }

        /**
         * options.setFocus: will make the current `field` gain focus.
         * options.setFocusOnFirst: will only set focus on the first invalid `field`.
         */
        if (options.setFocus || (options.setFocusOnFirst && isFirstInvalidField)) {
            //Set the flag to FALSE to prevent other inputs in the same form from gaining focus.
            isFirstInvalidField = false;

            field.focus();

            //Set a wait period after the first element has been validated, then reset the `isFirstInvalidField` flag.
            window.setTimeout(function() {
                isFirstInvalidField = true;
            }, 25);
        }

        return false;
    } else {
        clearValidity(field);

        return true;
    }
}

/**
 * Remove all validation. Should be called every time before revalidating form
 * @param {element} form - Form to be cleared
 * @returns {void}
 */
export function clearFormErrors(form) {
    if (form) {
        let invalidFields = form.querySelectorAll(`.${FIELD_INVALID_CLASS}`);

        [].forEach.call(invalidFields, clearValidity);
    }
}

/**
 * Hides the form's success feedback elements.
 */
export function clearFormSuccess(form) {
    if (form) {
        let successFeedbackEls = form.querySelectorAll(`.${FEEDBACK_SUCCESS_CLASS}`);

        [].forEach.call(successFeedbackEls, currentFeedback => currentFeedback.classList.remove(FEEDBACK_VISIBILITY_CLASS));
    }
}

/**
 * Displays validity `message` and styles for the provided `field`.
 */
export function displayValidity(field, message) {
    let feedbackEl = _getFeedbackEl(field);

    field.classList.add(FIELD_INVALID_CLASS);

    feedbackEl.textContent = message;
    feedbackEl.classList.add(FEEDBACK_VISIBILITY_CLASS);
}

/**
 * Removes all validation error messages and classes from the specified field.
 */
export function clearValidity(field) {
    field.classList.remove(FIELD_INVALID_CLASS);
    field.setCustomValidity('');

    _getFeedbackEl(field).classList.remove(FEEDBACK_VISIBILITY_CLASS);
}

/**
 * Loops through individual input fields within a provided `form` in search for validity.
 * Just calling `form.checkValidity()` may not always work cause it is not aware of custom validations that are set in the application.
 * @param  {[HTMLFormElement]} form [description]
 * @return {[Boolean]}      [description]
 */
export function checkFormValidity(form) {
    let isValid = true;

    [].forEach.call(_getFormFields(form), function(currentField) {
        if (_canValidateField(currentField)) {
            isValid = validateField(currentField, {setFocusOnFirst: true}) && isValid;
        }
    });

    if (form.classList.contains('checkout-stage__form')) {
        if (($('#email').length > 0 && $('#email').is(':visible')) && (!$('#email').val() || $('#email').hasClass('is-invalid'))) {
            $('#email').blur().focus();
            isValid = false;
        }
    }

    return isValid;
}

/**
 * Wrapper for _validateForm to avoid having to use `.call()` every time.
 */
export function validateForm(form, evt) {
    return _validateForm.call(form, evt);
}

/**
 * Provide an array of key-value pairs with the field's name as the key, and the error message as the value.
 * This key-value pair is used to set servser-side custom messages on the fields within the provided `containerEl`.
 * Example:
 * {"user_email": "This email is already in use."}
 * @param {[type]} containerEl [description]
 * @param {[type]} errorList   [description]
 */
export function setCustomErrors(containerEl, errorList) {
    if (errorList instanceof Array) {
        errorList.forEach(errorGroup => _setCustomErrorsByGroup(containerEl, errorGroup));
    } else {
        _setCustomErrorsByGroup(containerEl, errorList)
    }
}

/**
 * Loops through VALIDATION_LIST and attempts to confirm if the `field` is a match.
 * If a `validationName` is found, run it and set a custom message if the validaiton fails.
 */
export function getCustomValidation(field) {
    for (let validationName in VALIDATION_LIST) {
        if (_matchesValidationSelector(field, validationName)) {
            //End the loop if the validation failed.
            if (!VALIDATION_LIST[validationName].method(field.value, field, VALIDATION_LIST[validationName])) {
                //Check if the provided validation message is a function, in which case run it, otherwise use the message as-is.
                let messageIsFunction = typeof VALIDATION_LIST[validationName].message === 'function',
                    validationMessage = messageIsFunction ? VALIDATION_LIST[validationName].message(field.value, field) : VALIDATION_LIST[validationName].message;

                //When a field stores a message through `setCustomValidity()`,
                //that field is automatically  marked as invalid by the Validity API.
                field.setCustomValidity(validationMessage);

                break;
            }
        }
    }
}

/**
 * Adds a custom validation method and message to the VALIDATION_LIST object.
 */
export function addValidation(validationName, validationMethod, validationMessage) {
    VALIDATION_LIST[validationName] = {
        method: validationMethod,
        message: validationMessage
    };
}

/**
 * Configure custom messages to override the default ones from the browser.
 */
export function setDefaultMessages(messageObject) {
    for (let key in messageObject) {
        VALIDATION_MESSAGES[key] = messageObject[key];
    }
}

/**
 * Initializes event listening and validation for a single HTMLElement container in `form`.
 */
export function setupForm(form) {
    if (form.tagName === 'FORM') {
        form.addEventListener('submit', _validateForm);
    }

    [].forEach.call(_getFormFields(form), function(currentField) {
        _generateFeedbackEl(currentField);

        currentField.addEventListener('blur', validateField.bind(this, currentField));
        currentField.addEventListener('change', validateField.bind(this, currentField));
        currentField.addEventListener('invalid', _validateAndPreventDefault);
        currentField.addEventListener('input', _validateIfInvalid);
    });
}

/**
 * Calls `setupForm()` for each HTMLElement container available in `formList`.
 * @param  {[type]} formList [A DOM Selector or NodeList]
 */
export function setupFormList(formList = FORM_SELECTOR) {
    callbackOnElements(formList, setupForm);
}
/**
 * Call to Validate the BirthDay Date
 */
export function validateBirthDayDate(value) {
    let birthDayEle = document.getElementById('birthDay'),
        birthMonthEle = document.getElementById('birthMonth'),
        birthYearEle = document.getElementById('birthYear'),
        currentDate = new Date();
    if (birthDayEle && birthMonthEle && birthYearEle && value) {
        let birthDay = birthDayEle.value,
            birthMonth = birthMonthEle.value,
            birthYear = birthYearEle.value;
        if (birthDay == '' || birthMonth == '' || birthYear == '') {
            return true;
        }

        birthMonth = (parseInt(birthMonth) + 1).toString();

        let day = parseInt(birthDay);
        let month = parseInt(birthMonth);
        let year = parseInt(birthYear);
        // Create list of days of a month [assume there is no leap year by default]
        let listOfDays = [ 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31 ];
        if (month == 1 || month > 2) {
            if (day > listOfDays[month - 1]) {
                return false;
            }
        }
        if (month == 2) {
            var lyear = false;
            if ( (!(year % 4) && year % 100) || !(year % 400)) {
                lyear = true;
            }
            if ((lyear == false) && (day >= 29)) {
                return false;
            }
            if ((lyear == true) && (day > 29)) {
                return false;
            }
        }
        let birthDate = new Date(`${birthMonth}/${birthDay}/${birthYear}`);

        if (birthDate > currentDate) {
            return false;
        }
    }
    return true;
}
